// Session management utilities for authentication

const SESSION_KEY = 'tcc_session';
const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// Access codes (in production, these would be environment variables)
const SUBSCRIBER_CODE = process.env.REACT_APP_SUBSCRIBER_CODE || 'login';
const ADMIN_CODE = process.env.REACT_APP_ADMIN_CODE || 'admin';

/**
 * Validate an access code and return the role
 * @param {string} code - The access code entered by the user
 * @returns {{valid: boolean, role?: string, error?: string}}
 */
export function validateAccessCode(code) {
  if (!code || typeof code !== 'string') {
    return { valid: false, error: 'Please enter an access code.' };
  }

  const trimmedCode = code.trim().toLowerCase();

  if (trimmedCode === SUBSCRIBER_CODE.toLowerCase()) {
    return { valid: true, role: 'subscriber' };
  }

  if (trimmedCode === ADMIN_CODE.toLowerCase()) {
    return { valid: true, role: 'admin' };
  }

  return { valid: false, error: 'Invalid access code. Try again.' };
}

/**
 * Create a new session
 * @param {string} role - The user role (subscriber or admin)
 */
export function createSession(role) {
  const session = {
    role,
    expiresAt: Date.now() + SESSION_DURATION,
    createdAt: Date.now()
  };

  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

/**
 * Get the current session
 * @returns {{role: string, expiresAt: number, createdAt: number} | null}
 */
export function getSession() {
  try {
    const sessionStr = localStorage.getItem(SESSION_KEY);
    if (!sessionStr) return null;

    const session = JSON.parse(sessionStr);

    // Check if session is expired
    if (Date.now() > session.expiresAt) {
      clearSession();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error reading session:', error);
    clearSession();
    return null;
  }
}

/**
 * Check if there's a valid session
 * @returns {boolean}
 */
export function hasValidSession() {
  return getSession() !== null;
}

/**
 * Check if the current session has a specific role
 * @param {string} requiredRole - The required role to check
 * @returns {boolean}
 */
export function hasRole(requiredRole) {
  const session = getSession();
  return session && session.role === requiredRole;
}

/**
 * Clear the current session (logout)
 */
export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Check if the user is an admin
 * @returns {boolean}
 */
export function isAdmin() {
  return hasRole('admin');
}

/**
 * Check if the user is a subscriber (or admin, since admin has all permissions)
 * @returns {boolean}
 */
export function isSubscriber() {
  const session = getSession();
  return session && (session.role === 'subscriber' || session.role === 'admin');
}
